import { useSnackbar } from '@/contexts/SnackBarContext';
import SubAccountingService from '@/services/SubAccounting.service';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { FC, useCallback, useState } from 'react';

export const BulkRebalanceTab: FC = () => {
  const [rowBulkData, setRowBulkData] = useState('');
  const [participantIds, setParticipantIds] = useState<Array<string>>([]);
  const snackbar = useSnackbar();

  const bulkRebalance = useMutation(
    (ids: string[]) => SubAccountingService.bulkRebalance(ids),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: 'Error! Rebalance request could not be submitted',
          severity: 'error'
        });
      },
      onSuccess: res => {
        if (
          res.rejectedParticipants.length > 0 &&
          res.acceptedParticipants.length > 0
        ) {
          snackbar.showSnackbar({
            message: `The rebalance request have been submitted successfully, but participant(s) ${res.rejectedParticipants} in Goal-Based or 401(k) Manager cannot be rebalanced.`,
            severity: 'warning'
          });
        } else if (res.rejectedParticipants.length > 0) {
          snackbar.showSnackbar({
            message:
              'Error! Cannot rebalance savers in Goal-Based or 401(k) Manager.',
            severity: 'error'
          });
        } else {
          snackbar.showSnackbar({
            message: 'The rebalance request have been submitted successfully.',
            severity: 'success'
          });
        }
      }
    }
  );

  const handleSubmit = useCallback(() => {
    bulkRebalance.mutate(participantIds);
  }, [participantIds]);

  return (
    <>
      <Typography variant='h4'>Bulk Rebalance</Typography>
      <Grid container mt={2} spacing={2}>
        <Grid alignItems='flex-start' container direction='column' item xs={4}>
          <Typography variant='body1'>Participant IDs</Typography>
          <Typography mb={2} variant='body2'>
            Comma or line-break delimited
          </Typography>
          <Typography mb={2} variant='body2'>
            Participant(s) in Goal Models or 401(k) Manager cannot be rebalanced
            in Bulk
          </Typography>
          <TextField
            minRows={8}
            multiline
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setRowBulkData(event.target.value);
              const splitList = event.target.value
                .split(/[\r?\n,]/)
                .filter(element => element);
              setParticipantIds(splitList);
            }}
            sx={{ marginBottom: theme => theme.spacing(2) }}
            value={rowBulkData}></TextField>
          <Grid container item>
            <LoadingButton
              loading={bulkRebalance.isLoading}
              onClick={handleSubmit}
              variant='contained'>
              REBALANCE
            </LoadingButton>
            <Button
              disabled={bulkRebalance.isLoading}
              onClick={() => {
                setRowBulkData('');
              }}
              sx={{ marginLeft: theme => theme.spacing(2) }}
              variant='text'>
              RESET
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

BulkRebalanceTab.displayName = 'BulkRebalanceTab';
